import { useQuery } from '@tanstack/react-query';
import api from '../../../services';
import { fetchAll } from '../../../services/helpers';
import { EXCLUDED_OFFER_TYPE_IDS, type Offer } from './offerBlockModel';

/* eslint-disable @typescript-eslint/no-explicit-any -- API entities are untyped across the app */
const byName = (list: any[] = []) =>
  [...list].sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? '')));

export const useOfferBlockRecord = (offerBlockId: number | null) =>
  useQuery({
    queryKey: ['offerBlockBuilder', 'block', offerBlockId],
    queryFn: () => api.get(`offerblocks/${offerBlockId}`).then((res) => res.data),
    enabled: offerBlockId != null,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: 0,
  });

/** Primary-variant offers that can go into a block, as the classic editor's list loads them. */
export const useBlockOffers = () =>
  useQuery({
    queryKey: ['offerBlockBuilder', 'offers'],
    queryFn: async () => {
      const offers: Offer[] = await fetchAll('offers/summaries');
      return offers
        .filter((offer) => !EXCLUDED_OFFER_TYPE_IDS.includes(offer.offerType?.id as number))
        .filter((offer) => offer.isPrimaryVariant === true || offer.isPrimaryVariant == null)
        .sort((a, b) => b.id - a.id);
    },
    refetchOnWindowFocus: false,
  });

export const useBlockLayouts = () =>
  useQuery({
    queryKey: ['offerBlockBuilder', 'layouts'],
    queryFn: () => api.get('layouts/table').then((res) => res.data),
    select: byName,
    refetchOnWindowFocus: false,
  });

export const useBlockContainers = () =>
  useQuery({
    queryKey: ['offerBlockBuilder', 'containers'],
    queryFn: () => api.get('containers/table').then((res) => res.data),
    select: byName,
    refetchOnWindowFocus: false,
  });

/** Every primary offer's variations, keyed by primary offer id; drives the live/paused chips. */
export const useBlockVariationsMap = () =>
  useQuery({
    queryKey: ['offerBlockBuilder', 'variationsMap'],
    queryFn: () => api.get('offervariations/all-variations-map').then((res) => res.data ?? {}),
    refetchOnWindowFocus: false,
  });
